import Downshift from 'downshift'
import { isEmpty } from 'lodash'
import { includes, uniq } from 'lodash/fp'
import * as React from 'react'
import { Keyword } from 'scenes/Dashboard/components-next/Keyword'
import { ExtendableStyles, Testable } from 'utils-next/types'

import { Box, InputAdornment, styled, TextField, TextFieldProps } from '@mui/material'

export type TagsInputProps = {
  tags?: string
  handleSelectedTags?: (item: string[]) => void
  label?: React.ReactNode
  sentiment?: 'positive' | 'negative'
  fullWidth?: boolean
} & Omit<TextFieldProps, 'label'> &
  ExtendableStyles &
  Testable
/* Local utility functions */

const stringToArray = (str: string) => {
  return Array.isArray(str) ? str : str ? str.split(',') : []
}

const KEY_SEPARATORS = ['Enter', ';', ',']
/* Component definition */
export const TagsInput = ({
  tags = '',
  color,
  placeholder,
  handleSelectedTags,
  sentiment,
  variant,
  label,
  onChange,
  fullWidth = false,
  ...rest
}: TagsInputProps) => {
  const [selectedItem, setSelectedItem] = React.useState<string[]>(stringToArray(tags))
  const [inputValue, setInputValue] = React.useState('')

  const callHandleSelectedTags = React.useCallback(() => {
    if (handleSelectedTags) {
      handleSelectedTags(selectedItem)
    }
  }, [selectedItem])

  React.useEffect(() => {
    callHandleSelectedTags()
  }, [selectedItem])

  const handleOnKeyDown = (event: any) => {
    if (includes(event.key, KEY_SEPARATORS) || event.type === 'blur') {
      event.preventDefault()
      const newSelectedItems = [...selectedItem]

      // avoid adding empty strings
      if (!event.target.value.replace(/\s/g, '').length) {
        return
      }

      newSelectedItems.push(event.target.value.trim())
      setSelectedItem(uniq(newSelectedItems))
      setInputValue('')
    }

    if (selectedItem.length && !inputValue.length && event.key === 'Backspace') {
      setSelectedItem(selectedItem.slice(0, selectedItem.length - 1))
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(event.target.value)
  }

  const handleDelete = (item: string) => () => {
    const newSelectedItem = [...selectedItem]
    newSelectedItem.splice(newSelectedItem.indexOf(item), 1)

    setSelectedItem(newSelectedItem)
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event)
    }
  }

  return (
    <React.Fragment>
      <Downshift id="tags-input" inputValue={inputValue} selectedItem={selectedItem}>
        {({ getInputProps, getRootProps }) => {
          const { onChange, onBlur, onFocus, color, ref, size, ...inputProps } = getInputProps({
            onKeyDown: handleOnKeyDown,
            placeholder: isEmpty(selectedItem) ? placeholder : '',
            onChange: handleOnChange,
          })

          return (
            <S.TagsInputWrapper {...getRootProps()} fullWidth={fullWidth}>
              {label && <S.Label>{label}</S.Label>}
              <S.InputAdornment position="start">
                {selectedItem.map((tag) => (
                  <S.Keyword
                    key={tag}
                    sentiment={sentiment}
                    tabIndex={-1}
                    label={tag}
                    onDelete={handleDelete(tag)}
                  ></S.Keyword>
                ))}

                <TextField
                  color="primary"
                  variant="standard"
                  type="text"
                  focused
                  sx={{
                    '&.MuiFormControl-root': {
                      width: '100%',
                      marginBottom: 0,
                      flex: '1',
                    },
                  }}
                  inputProps={{
                    sx: {
                      marginBottom: 0,
                    },
                  }}
                  InputProps={{
                    onBlur: handleOnKeyDown,
                    onChange: (event) => {
                      if (onChange) {
                        onChange(event as React.ChangeEvent<HTMLInputElement>)
                      }
                      handleInputChange(event)
                    },
                    onFocus,
                    disableUnderline: true,
                  }}
                  {...rest}
                  {...inputProps}
                />
              </S.InputAdornment>
            </S.TagsInputWrapper>
          )
        }}
      </Downshift>
    </React.Fragment>
  )
}

/* Styles */

const S = {
  Label: styled('label')`
    font-size: calc(1rem * 0.75);
    color: rgba(0, 0, 0, 0.6);
  `,
  Keyword: styled(Keyword)`
    margin: ${(props) => props.theme.spacing(1, 0.25)};
  `,
  TagsInputWrapper: styled(Box)<{ fullWidth?: boolean }>`
    min-height: ${(props) => props.theme.spacing(10)};
    width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
  `,
  InputAdornment: styled(InputAdornment)`
    align-items: center;
    flex-wrap: wrap;
    height: auto;
    max-height: 100%;
    margin-bottom: ${(props) => props.theme.spacing(1)};
    border-bottom: 1px solid currentColor;
  `,
}
